import React, { useState, useEffect, useCallback } from 'react';
import { Icon } from '@iconify/react';
import { Modal, Form, Tab, Tabs, Row, Col } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import config from '../config';
import { toast } from 'react-toastify';
import VenuePreviewModal from './VenuePreviewModal';
import { formatDisplayDate, formatDisplayDateTime } from '../utils/formatDate';

const VenueVerificationLayer = () => {
    const { auth } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const [stats, setStats] = useState({ total: 0, pending: 0, approved: 0, rejected: 0, suspended: 0, banned: 0 });
    const [venues, setVenues] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({
        status: 'pending',
        search: '',
        page: 1,
        limit: 10
    });
    const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });

    // Modal State
    const [showModal, setShowModal] = useState(false);
    const [selectedVenue, setSelectedVenue] = useState(null);
    const [venueDetails, setVenueDetails] = useState(null);
    const [detailsLoading, setDetailsLoading] = useState(false);
    
    // Action State
    const [actionModal, setActionModal] = useState(false);
    const [actionType, setActionType] = useState(null); // 'approved' | 'rejected'
    const [actionReason, setActionReason] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Request Info Modal State
    const [showRequestInfoModal, setShowRequestInfoModal] = useState(false);
    const [requestInfoTo, setRequestInfoTo] = useState('');
    const [requestInfoSubject, setRequestInfoSubject] = useState('');
    const [requestInfoMessage, setRequestInfoMessage] = useState('');
    const [requestInfoLoading, setRequestInfoLoading] = useState(false);

    // Preview State
    const [showPreview, setShowPreview] = useState(false);
    const [previewId, setPreviewId] = useState(null);

    const getOwnerImageUrl = (imagePath) => {
        if (!imagePath) return null;
        if (imagePath.startsWith('http')) return imagePath;
        if (imagePath.includes('/avatar/')) {
            return `${config.API_BASE_URL}${imagePath.startsWith('/') ? '' : '/'}${imagePath}`;
        }
        return imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
    };

    // Fetch Stats
    const fetchStats = useCallback(async () => {
        try {
            const res = await auth.fetch(`${config.API_BASE_URL}/api/admin/venues/stats`);
            const data = await res.json();
            setStats(data);
        } catch (error) {
            console.error('Error fetching stats:', error);
        }
    }, [auth]);

    // Fetch Venues
    const fetchVenues = useCallback(async () => {
        setLoading(true);
        try {
            const query = new URLSearchParams({
                status: filters.status === 'all' ? '' : filters.status,
                search: filters.search,
                page: filters.page,
                limit: filters.limit
            });
            
            const res = await auth.fetch(`${config.API_BASE_URL}/api/admin/venues?${query}`);
            const data = await res.json();
            setVenues(data.data || []);
            if (data.pagination) {
                setPagination(data.pagination);
            }
        } catch (error) {
            console.error('Error fetching venues:', error);
            toast.error('Failed to load venues');
        } finally {
            setLoading(false);
        }
    }, [auth, filters]);

    // Fetch Venue Details
    const fetchVenueDetails = async (id) => {
        setDetailsLoading(true);
        try {
            const res = await auth.fetch(`${config.API_BASE_URL}/api/admin/venues/${id}`);
            const data = await res.json();
            setVenueDetails(data);
        } catch (error) {
            console.error('Error fetching venue details:', error);
            toast.error('Failed to load venue details');
        } finally {
            setDetailsLoading(false);
        }
    };

    useEffect(() => {
        fetchStats();
        fetchVenues();
    }, [fetchStats, fetchVenues]);

    // Handle return navigation from editing/managing venue
    useEffect(() => {
        if (location.state?.openVenueId) {
            const vId = location.state.openVenueId;
            setSelectedVenue({ id: vId });
            setShowModal(true);
            fetchVenueDetails(vId);
        }
    }, [location.state]);

    const handleView = (venue) => {
        setSelectedVenue(venue);
        setShowModal(true);
        fetchVenueDetails(venue.id);
    };

    const handleReopenVenue = async () => {
        if (!selectedVenue || !window.confirm('Let the owner edit this venue? It will lock again once they save.')) return;
        try {
            const response = await auth.fetch(`${config.API_BASE_URL}/api/admin/venues/${selectedVenue.id}/reopen`, { method: 'POST' });
            const result = await response.json().catch(() => ({}));
            if (!response.ok) throw new Error(result.message || 'Could not reopen the venue');
            toast.success('Venue reopened. The owner has been notified.');
        } catch (error) {
            console.error('Error reopening venue:', error);
            toast.error(error.message);
        }
    };

    const handleOpenRequestInfo = (venue) => {
        const ownerEmail = venue?.owner_email || (venueDetails?.venue?.email) || '';
        const ownerName = venue?.owner_name || venue?.business_name || 'Partner';
        const venueTitle = venue?.title || venue?.business_name || 'your venue';
        setRequestInfoTo(ownerEmail);
        setRequestInfoSubject('HubScroll – Additional Information Required');
        setRequestInfoMessage(
            `Dear ${ownerName},\n\n` +
            `We are currently reviewing your venue verification request for "${venueTitle}". ` +
            `Before we can finalize verification and make your venue live on HubScroll, we require some additional details:\n\n` +
            `- [Please specify required details or documents here]\n\n` +
            `Please reply directly to this email or update your venue details via your HubScroll dashboard.\n\n` +
            `Best regards,\nHubScroll Operations Team`
        );
        setShowRequestInfoModal(true);
    };

    const handleSendRequestInfo = async () => {
        if (!requestInfoTo || !requestInfoSubject || !requestInfoMessage.trim()) {
            toast.error('Please fill in all email fields');
            return;
        }
        setRequestInfoLoading(true);
        try {
            const res = await auth.fetch(`${config.API_BASE_URL}/api/email/send`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    to: requestInfoTo,
                    subject: requestInfoSubject,
                    message: requestInfoMessage
                })
            });
            const data = await res.json();
            if (res.ok) {
                toast.success(`Information request email sent successfully to ${requestInfoTo}`);
                setShowRequestInfoModal(false);
            } else {
                toast.error(data.error || 'Failed to send email');
            }
        } catch (err) {
            toast.error('Error sending email: ' + err.message);
        } finally {
            setRequestInfoLoading(false);
        }
    };

    const handleActionClick = (type) => {
        setActionType(type);
        setActionReason('');
        setActionModal(true);
    };

    const submitAction = async () => {
        if (!selectedVenue || !actionType) return;
        
        setIsSubmitting(true);
        try {
            const response = await auth.fetch(`${config.API_BASE_URL}/api/admin/venues/${selectedVenue.id}/verify`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    status: actionType,
                    notes: actionReason
                })
            });
            const result = await response.json().catch(() => ({}));
            if (!response.ok) {
                throw new Error(result.message || result.error || 'Verification action failed');
            }
            
            toast.success(`Venue ${actionType} successfully`);
            setShowModal(false);
            setActionModal(false);
            fetchVenues();
            fetchStats();
        } catch (error) {
            console.error('Error performing action:', error);
            toast.error(error.message || 'Action failed');
        } finally {
            setIsSubmitting(false);
        }
    };

    const getStatusBadge = (status) => {
        const variants = {
            pending: { cls: 'hs-badge-warning', icon: 'solar:clock-circle-bold', label: 'Pending' },
            approved: { cls: 'hs-badge-success', icon: 'solar:check-circle-bold', label: 'Approved' },
            rejected: { cls: 'hs-badge-danger', icon: 'solar:close-circle-bold', label: 'Rejected' },
            suspended: { cls: 'hs-badge-neutral', icon: 'solar:danger-triangle-bold', label: 'Suspended' },
            banned: { cls: 'hs-badge-danger', icon: 'solar:shield-cross-bold', label: 'Banned' }
        };
        const s = variants[status] || variants.pending;
        return (
            <span className={`hs-badge ${s.cls}`}>
                <Icon icon={s.icon} className="text-xs" />
                {s.label}
            </span>
        );
    };

    return (
        <div className="d-flex flex-column gap-20">
            {/* Stats Cards */}
            <div className="row g-3">
                {[
                    { title: 'Pending Approval', status: 'pending', value: stats.pending, icon: 'solar:clock-circle-bold', accent: 'var(--hs-warning)', color: 'text-warning-600', bg: 'bg-warning-50' },
                    { title: 'Approved Venues', status: 'approved', value: stats.approved, icon: 'solar:check-circle-bold', accent: 'var(--hs-success)', color: 'text-success-600', bg: 'bg-success-50' },
                    { title: 'Rejected Venues', status: 'rejected', value: stats.rejected, icon: 'solar:close-circle-bold', accent: 'var(--hs-danger)', color: 'text-danger-600', bg: 'bg-danger-50' },
                    { title: 'Suspended', status: 'suspended', value: stats.suspended, icon: 'solar:danger-triangle-bold', accent: 'var(--hs-warning)', color: 'text-warning-600', bg: 'bg-warning-50' },
                    { title: 'Banned', status: 'banned', value: stats.banned, icon: 'solar:shield-cross-bold', accent: 'var(--hs-danger)', color: 'text-danger-600', bg: 'bg-danger-50' },
                    { title: 'Total Registered', status: 'all', value: stats.total, icon: 'solar:shop-2-bold', accent: 'var(--hs-primary)', color: 'text-primary-600', bg: 'bg-primary-50' },
                ].map((stat, idx) => (
                    <div 
                        className="col-xxl-2 col-sm-6"
                        key={idx}
                        onClick={() => setFilters(prev => ({ ...prev, status: stat.status, page: 1 }))}
                        style={{ cursor: 'pointer' }}
                    >
                        <div 
                            className={`hs-stat-card h-100 ${filters.status === stat.status ? 'border-primary' : ''}`} 
                            style={{ 
                                '--stat-accent': stat.accent,
                                transform: filters.status === stat.status ? 'scale(1.02)' : 'none',
                                transition: 'all 0.2s ease',
                                boxShadow: filters.status === stat.status ? '0 4px 12px rgba(0,0,0,0.08)' : undefined
                            }}
                        >
                            <div className={`hs-stat-icon-wrapper ${stat.bg} ${stat.color}`}>
                                <Icon icon={stat.icon} />
                            </div>
                            <div>
                                <span className="text-secondary-light text-xs fw-semibold text-uppercase d-block mb-1">{stat.title}</span>
                                <h4 className="fw-bold mb-0 text-dark">{stat.value}</h4>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Filter & List */}
            <div className="hs-card">
                <div className="hs-card-header flex-column flex-md-row align-items-stretch align-items-md-center gap-3">
                    <div className="d-flex align-items-center gap-2">
                        <Icon icon="solar:shield-check-bold" className="text-primary-600 text-xl" />
                        <h6 className="fw-bold mb-0 text-dark">Venue Verification Queue</h6>
                    </div>

                    <div className="d-flex align-items-center flex-wrap gap-2">
                        <div className="hs-search-wrapper" style={{ maxWidth: '240px' }}>
                            <Icon icon="solar:magnifer-linear" className="hs-search-icon" />
                            <input
                                type="text"
                                className="hs-input"
                                placeholder="Search venue or owner..."
                                value={filters.search}
                                onChange={(e) => setFilters({...filters, search: e.target.value, page: 1})}
                            />
                        </div>
                        <select 
                            className="hs-select w-auto" 
                            value={filters.status}
                            onChange={(e) => setFilters({...filters, status: e.target.value, page: 1})}
                        >
                            <option value="all">All Statuses</option>
                            <option value="pending">Pending Approval</option>
                            <option value="approved">Approved</option>
                            <option value="rejected">Rejected</option>
                            <option value="suspended">Suspended</option>
                            <option value="banned">Banned</option>
                        </select>
                        <select 
                            className="hs-select w-auto" 
                            value={filters.limit}
                            onChange={(e) => setFilters({...filters, limit: Number(e.target.value), page: 1})}
                        >
                            <option value="10">10 per page</option>
                            <option value="20">20 per page</option>
                            <option value="50">50 per page</option>
                        </select>
                    </div>
                </div>

                <div className="hs-card-body p-0">
                    <div className="hs-table-wrapper">
                        <table className="hs-table mb-0">
                            <thead>
                                <tr>
                                    <th>Venue Details</th>
                                    <th>Owner Contact</th>
                                    <th>Submission Date</th>
                                    <th>Verification Status</th>
                                    <th className="text-end">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr><td colSpan="5" className="text-center p-24 text-secondary-light">Loading venues...</td></tr>
                                ) : venues.length === 0 ? (
                                    <tr><td colSpan="5" className="text-center p-24 text-secondary-light">No venues found matching current filters.</td></tr>
                                ) : (
                                    venues.map(venue => (
                                        <tr key={venue.id}>
                                            <td>
                                                <div className="d-flex align-items-center">
                                                    <div className="flex-grow-1">
                                                        <h6 className="text-sm mb-1 fw-bold text-dark">{venue.title}</h6>
                                                        <span className="text-xs text-secondary-light">{venue.business_name}</span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="d-flex align-items-center">
                                                    {getOwnerImageUrl(venue.owner_image) && (
                                                        <img
                                                            src={getOwnerImageUrl(venue.owner_image)}
                                                            alt=""
                                                            className="w-32-px h-32-px rounded-circle me-2 object-fit-cover shadow-2xs border"
                                                            onError={(e) => {
                                                                e.target.src = '/assets/images/user.png';
                                                            }}
                                                        />
                                                    )}
                                                    <div>
                                                        <h6 className="text-sm mb-0 fw-medium text-dark">{venue.owner_name}</h6>
                                                        <span className="text-xs text-secondary-light">{venue.owner_email}</span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="text-secondary-light text-sm">{formatDisplayDate(venue.created_at)}</td>
                                            <td>{getStatusBadge(venue.verification_status)}</td>
                                            <td className="text-end">
                                                {venue.verification_status === 'pending' ? (
                                                    <button 
                                                        type="button"
                                                        className="btn btn-sm text-white px-3 py-1 radius-8 fw-semibold d-inline-flex align-items-center gap-1 shadow-none"
                                                        style={{ backgroundColor: '#0388d6', border: 'none' }}
                                                        onClick={() => handleView(venue)}
                                                        title="Review Venue Verification"
                                                    >
                                                        <Icon icon="solar:shield-check-bold" />
                                                        <span>Review</span>
                                                    </button>
                                                ) : (
                                                    <button 
                                                        type="button"
                                                        className="btn btn-sm btn-outline-secondary px-3 py-1 radius-8 fw-semibold d-inline-flex align-items-center gap-1 shadow-none"
                                                        onClick={() => handleView(venue)}
                                                        title="View Venue Details"
                                                    >
                                                        <Icon icon="solar:eye-bold" />
                                                        <span>View</span>
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                    
                    {/* Pagination */}
                    {pagination.totalPages > 1 && (
                        <div className="d-flex align-items-center justify-content-between p-3 border-top">
                            <span className="text-sm text-secondary-light">
                                Showing {((filters.page - 1) * filters.limit) + 1} to {Math.min(filters.page * filters.limit, pagination.total)} of {pagination.total} entries
                            </span>
                            <div className="pagination mb-0">
                                <button 
                                    className="page-link" 
                                    disabled={filters.page === 1}
                                    onClick={() => setFilters({...filters, page: filters.page - 1})}
                                >
                                    Previous
                                </button>
                                <span className="page-link active">{filters.page}</span>
                                <button 
                                    className="page-link" 
                                    disabled={filters.page === pagination.totalPages}
                                    onClick={() => setFilters({...filters, page: filters.page + 1})}
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Detail / Review Modal */}
            <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title className="text-dark fw-bold">
                        {selectedVenue?.verification_status === 'pending' ? 'Review Venue Verification' : 'Venue Verification Details'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {detailsLoading || !venueDetails ? (
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary me-2" role="status" />
                            <span className="text-secondary-light">Loading venue details...</span>
                        </div>
                    ) : (
                        <Tabs defaultActiveKey="info" className="mb-3">
                            <Tab eventKey="info" title="Information">
                                <Row className="g-3">
                                    <Col md={6}>
                                        <label className="form-label text-secondary-light text-xs">Business Name</label>
                                        <p className="fw-medium mb-0">{venueDetails.venue.business_name}</p>
                                    </Col>
                                    <Col md={6}>
                                        <label className="form-label text-secondary-light text-xs">Title</label>
                                        <p className="fw-medium mb-0">{venueDetails.venue.title}</p>
                                    </Col>
                                    <Col md={6}>
                                        <label className="form-label text-secondary-light text-xs">License Number</label>
                                        <p className="fw-medium mb-0">{venueDetails.venue.license_number || 'N/A'}</p>
                                    </Col>
                                    <Col md={6}>
                                        <label className="form-label text-secondary-light text-xs">Category</label>
                                        <p className="fw-medium mb-0">{venueDetails.venue.business_category || 'N/A'}</p>
                                    </Col>
                                    <Col md={12}>
                                        <label className="form-label text-secondary-light text-xs">Description</label>
                                        <p className="text-sm mb-0">{venueDetails.venue.description || 'No description provided.'}</p>
                                    </Col>
                                    <Col md={12}>
                                        <label className="form-label text-secondary-light text-xs">Address</label>
                                        <p className="text-sm mb-0">
                                            {[venueDetails.venue.street, venueDetails.venue.city, venueDetails.venue.state, venueDetails.venue.country].filter(Boolean).join(', ') || 'N/A'}
                                        </p>
                                    </Col>
                                    <Col md={6}>
                                        <label className="form-label text-secondary-light text-xs">Owner / Contact Email</label>
                                        <p className="fw-medium mb-0">{venueDetails.venue.email || selectedVenue?.owner_email || 'N/A'}</p>
                                    </Col>
                                    <Col md={6}>
                                        <label className="form-label text-secondary-light text-xs">Phone</label>
                                        <p className="fw-medium mb-0">{venueDetails.venue.phone || 'N/A'}</p>
                                    </Col>
                                </Row>
                            </Tab>
                            <Tab eventKey="documents" title={`Documents (${venueDetails.documents?.length || 0})`}>
                                {!venueDetails.documents || venueDetails.documents.length === 0 ? (
                                    <p className="text-center text-secondary-light py-4 mb-0">No verification documents uploaded</p>
                                ) : (
                                    <div className="d-flex flex-column gap-3">
                                        {venueDetails.documents.map(doc => (
                                            <div key={doc.id} className="d-flex align-items-center justify-content-between p-3 border radius-8">
                                                <div className="d-flex align-items-center gap-2">
                                                    <Icon icon="solar:document-text-bold" className="text-2xl text-primary-600" />
                                                    <div>
                                                        <p className="mb-0 fw-medium">{doc.document_type || 'Document'}</p>
                                                        <span className="text-xs text-secondary-light">{formatDisplayDate(doc.uploaded_at)}</span>
                                                    </div>
                                                </div>
                                                <a href={doc.file_url} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-primary">
                                                    View
                                                </a>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </Tab>
                            <Tab eventKey="gallery" title={`Gallery (${venueDetails.gallery?.length || 0})`}>
                                {!venueDetails.gallery || venueDetails.gallery.length === 0 ? (
                                    <p className="text-center text-secondary-light py-4 mb-0">No gallery images uploaded</p>
                                ) : (
                                    <div className="row g-2">
                                        {venueDetails.gallery.map(img => (
                                            <div className="col-4" key={img.id}>
                                                <img 
                                                    src={img.image_url?.startsWith('http') ? img.image_url : `${config.API_BASE_URL}/${img.image_url}`} 
                                                    alt="" 
                                                    className="w-100 h-100 object-fit-cover radius-8 border" 
                                                    style={{minHeight: '110px'}} 
                                                />
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </Tab>
                            <Tab eventKey="logs" title="Audit Trail">
                                {!venueDetails.logs || venueDetails.logs.length === 0 ? (
                                    <p className="text-center text-secondary-light py-4 mb-0">No status audit history</p>
                                ) : (
                                    <div className="timeline">
                                        {venueDetails.logs.map(log => (
                                            <div key={log.id} className="d-flex gap-3 mb-3">
                                                <div className="d-flex flex-column align-items-center">
                                                    <div className="w-8-px h-8-px bg-primary-600 rounded-circle mt-1"></div>
                                                    <div className="w-1-px bg-neutral-200 flex-grow-1"></div>
                                                </div>
                                                <div>
                                                    <p className="mb-0 fw-medium text-sm">Status changed to <span className="fw-bold text-dark">{log.new_status}</span></p>
                                                    <p className="text-xs text-secondary-light mb-1">by {log.admin_name || 'Admin'} on {formatDisplayDateTime(log.created_at)}</p>
                                                    {log.notes && <p className="text-xs bg-light p-2 radius-4 text-secondary mb-0">{log.notes}</p>}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </Tab>
                        </Tabs>
                    )}
                </Modal.Body>
                <Modal.Footer className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                        {selectedVenue && (
                            <>
                                <button 
                                    type="button"
                                    className="btn btn-sm text-white px-3 py-2 radius-8 fw-semibold d-inline-flex align-items-center gap-1 shadow-none"
                                    style={{ backgroundColor: '#0388d6', border: 'none' }}
                                    onClick={() => navigate(`/edit-venue/${selectedVenue.id}`, { state: { returnTo: '/venue-verification', openVenueId: selectedVenue.id } })}
                                >
                                    <Icon icon="solar:pen-bold" />
                                    <span>Edit Info</span>
                                </button>
                                <button 
                                    type="button"
                                    className="btn btn-sm text-white px-3 py-2 radius-8 fw-semibold d-inline-flex align-items-center gap-1 shadow-none"
                                    style={{ backgroundColor: '#0388d6', border: 'none' }}
                                    onClick={() => navigate(`/venue/${selectedVenue.id}`, { state: { returnTo: '/venue-verification', openVenueId: selectedVenue.id } })}
                                >
                                    <Icon icon="solar:settings-bold" />
                                    <span>Manage Menu & Gallery</span>
                                </button>
                                <button 
                                    type="button"
                                    className="btn btn-sm text-white px-3 py-2 radius-8 fw-semibold d-inline-flex align-items-center gap-1 shadow-none"
                                    style={{ backgroundColor: '#0388d6', border: 'none' }}
                                    onClick={() => {
                                        setPreviewId(selectedVenue.id);
                                        setShowPreview(true);
                                    }}
                                >
                                    <Icon icon="solar:eye-bold" />
                                    <span>Live Preview</span>
                                </button>
                                <button 
                                    type="button"
                                    className="btn btn-sm text-white px-3 py-2 radius-8 fw-semibold d-inline-flex align-items-center gap-1 shadow-none"
                                    style={{ backgroundColor: '#0284c7', border: 'none' }}
                                    onClick={() => handleOpenRequestInfo(selectedVenue)}
                                >
                                    <Icon icon="solar:letter-bold" />
                                    <span>Request Info</span>
                                </button>
                                {(selectedVenue.verification_status || 'pending') === 'pending' && (
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-secondary px-3 py-2 radius-8 fw-semibold d-inline-flex align-items-center gap-2"
                                        onClick={handleReopenVenue}
                                    >
                                        <Icon icon="solar:lock-unlocked-bold" />
                                        <span>Reopen for Editing</span>
                                    </button>
                                )}
                            </>
                        )}
                    </div>
                    
                    <div className="d-flex align-items-center gap-2">
                        {selectedVenue && selectedVenue.verification_status !== 'banned' && (
                            <>
                                {selectedVenue.verification_status !== 'rejected' && (
                                    <button 
                                        type="button"
                                        className="btn btn-sm text-white px-3 py-2 radius-8 fw-semibold d-inline-flex align-items-center gap-1 shadow-none"
                                        style={{ backgroundColor: '#dc2626', border: 'none' }}
                                        onClick={() => handleActionClick('rejected')}
                                    >
                                        <Icon icon="solar:close-circle-bold" />
                                        <span>{selectedVenue.verification_status === 'approved' ? 'Revoke / Reject' : 'Reject'}</span>
                                    </button>
                                )}
                                {selectedVenue.verification_status !== 'approved' && (
                                    <button 
                                        type="button"
                                        className="btn btn-sm text-white px-3 py-2 radius-8 fw-semibold d-inline-flex align-items-center gap-1 shadow-none"
                                        style={{ backgroundColor: '#16a34a', border: 'none' }}
                                        onClick={() => handleActionClick('approved')}
                                    >
                                        <Icon icon="solar:check-circle-bold" />
                                        <span>{selectedVenue.verification_status === 'rejected' ? 'Reconsider / Approve' : 'Approve'}</span>
                                    </button>
                                )}
                            </>
                        )}
                    </div>
                </Modal.Footer>
            </Modal>

            {/* Action Confirmation Modal (Approve / Reject) */}
            <Modal show={actionModal} onHide={() => setActionModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title className="fw-bold">Confirm {actionType === 'approved' ? 'Approval' : 'Rejection'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="text-secondary-dark text-sm mb-3">
                        {actionType === 'approved' 
                            ? 'Approving this venue will immediately publish it live on the HubScroll platform and notify the venue owner by email.'
                            : 'Rejecting this venue will notify the owner by email with the reason provided below.'}
                    </p>
                    <Form.Group className="mb-2">
                        <Form.Label className="fw-semibold text-xs text-dark">
                            {actionType === 'rejected' ? 'Rejection Reason (Required)' : 'Verification Notes (Optional)'}
                        </Form.Label>
                        <Form.Control 
                            as="textarea" 
                            rows={3} 
                            value={actionReason} 
                            onChange={(e) => setActionReason(e.target.value)}
                            placeholder={actionType === 'rejected' ? 'State the exact reason for rejection so the owner can resolve it...' : 'Internal notes or approval remarks...'}
                            required={actionType === 'rejected'}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <button 
                        type="button"
                        className="hs-btn-secondary"
                        onClick={() => setActionModal(false)}
                    >
                        Cancel
                    </button>
                    <button 
                        type="button"
                        className="btn btn-sm text-white px-3 py-2 radius-8 fw-semibold" 
                        style={{ backgroundColor: actionType === 'approved' ? '#16a34a' : '#dc2626', border: 'none' }}
                        onClick={submitAction}
                        disabled={isSubmitting || (actionType === 'rejected' && !actionReason.trim())}
                    >
                        {isSubmitting ? 'Processing...' : 'Confirm'}
                    </button>
                </Modal.Footer>
            </Modal>

            {/* Request Information Email Modal */}
            <Modal show={showRequestInfoModal} onHide={() => setShowRequestInfoModal(false)} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title className="fw-bold d-flex align-items-center gap-2">
                        <Icon icon="solar:letter-bold" className="text-primary-600" />
                        <span>Request Information from Venue Owner</span>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <label className="form-label fw-semibold text-xs text-dark">Recipient Email (To)</label>
                        <input 
                            type="email" 
                            className="form-control" 
                            value={requestInfoTo} 
                            onChange={(e) => setRequestInfoTo(e.target.value)}
                            placeholder="owner@example.com"
                        />
                    </div>
                    <div className="mb-3">
                        <label className="form-label fw-semibold text-xs text-dark">Subject</label>
                        <input 
                            type="text" 
                            className="form-control" 
                            value={requestInfoSubject} 
                            onChange={(e) => setRequestInfoSubject(e.target.value)}
                        />
                    </div>
                    <div className="mb-2">
                        <label className="form-label fw-semibold text-xs text-dark">Message</label>
                        <textarea 
                            className="form-control" 
                            rows={8} 
                            value={requestInfoMessage} 
                            onChange={(e) => setRequestInfoMessage(e.target.value)}
                        />
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <button 
                        type="button" 
                        className="hs-btn-secondary"
                        onClick={() => setShowRequestInfoModal(false)}
                    >
                        Cancel
                    </button>
                    <button 
                        type="button" 
                        className="btn btn-sm text-white px-3 py-2 radius-8 fw-semibold d-inline-flex align-items-center gap-1 shadow-none"
                        style={{ backgroundColor: '#0388d6', border: 'none' }}
                        disabled={requestInfoLoading}
                        onClick={handleSendRequestInfo}
                    >
                        {requestInfoLoading ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-1" role="status" />
                                Sending...
                            </>
                        ) : (
                            <>
                                <Icon icon="solar:plain-bold" />
                                Send Information Request
                            </>
                        )}
                    </button>
                </Modal.Footer>
            </Modal>

            {/* Live Preview Modal */}
            <VenuePreviewModal 
                show={showPreview} 
                onHide={() => setShowPreview(false)} 
                venueId={previewId} 
            />
        </div>
    );
};

export default VenueVerificationLayer;
